/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/**
 *
 * PaymentMethods
 *
 */

import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { findIndex, get } from 'lodash';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';

import {
  Button,
  Popup,
  ConfirmModal,
  Conditional,
  Text,
  AlertModal,
  Space,
  Table,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  TableBody,
} from '@cashfree-intl/coherent';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import { useQuery, titleCase } from 'utils/common';
import EVENTS from '../../constants/analytics';
import Analytics from '../../utils/analytics';

import makeSelectPaymentMethods from './selectors';
import reducer from './reducer';
import saga from './saga';

import {
  enableLoaderAction,
  getPaymentMethodsAction,
  getPaypalConfigAction,
  resetPaymentMethodCardLoaderAction,
  initiatePaypalAction,
  activatePaypalCurrencyAction,
  deactivatePaypalCurrencyAction,
  updateDefaultPaypalCurrencyAction,
  savePaypalDataAction,
  resetPaypalConfigAction,
  resetDataAction,
  enablePaymentMethodAction,
  getPaytmDataAction,
  getPaytmTestConfigAction,
  getPaytmCheckSumHashAction,
  addPaytmProdConfigAction,
  addPaytmTestConfigAction,
  resetPaytmWalletAction,
  getDocsPerPaymentMethodAction,
  saveUploadDocumentsFormAction,
  downloadDocumentAction,
} from './actions';

import {
  PAYMENT_METHODS,
  NET_BANKING_PAYMENT_METHODS,
  CORPORATE_CREDIT_CARD_PAYMENT_METHODS,
  WALLET,
  PAYPAL,
  PAYTM,
  RESET_PAYPAL_MODAL,
  RESET_PAYTM_MODAL,
  PAYMENT_METHODS_HEADING,
  STANDARD_PRICING_LINK,
  IN_REVIEW,
  REJECTED,
  UPI_PAYMENT_METHODS,
  PAYMENT_METHODS_ICON_MAPPER,
  EMI_ON_CARD_PAYMENT_METHODS,
  ACTIVATE_PAYMENT_METHODS_LINK,
} from './constants';

import Loader from '../../components/Loader';
import CustomIcons from '../../components/CustomIcons';
import BankListModal from './components/BankListModal';
import Paypal from './components/Paypal';
import Paytm from './components/Paytm';
import PaymentMethodHistory from './components/PaymentMethodHistory';

import {
  resetTwoFactorAuthentication as resetTwoFactorAuthenticationValue,
  showToast as showToastAction,
} from '../Toast/actions';
import { twoFactorAuthenticationSelector } from '../Toast/selectors';
import EnablePaymentMethods from './components/EnablePaymentMethods/index';

const getHeadingText = heading => {
  switch (heading.key) {
    case 'requestedModes': {
      return (
        <Space gap={1} alignItems="center">
          <Text variant="b14" strong>
            {heading.displayText}
          </Text>
          <Text variant="b14" strong color="warning">
            (IN REVIEW)
          </Text>
        </Space>
      );
    }
    case 'otherAvailableModes': {
      return (
        <Space gap={1} alignItems="center">
          <Text variant="b14" strong>
            {heading.displayText}
          </Text>
          <Popup
            hoverable
            content={
              <Text variant="b12" color="bodyLight">
                Some payment methods can be enabled using ‘Enable Payment Modes’
                button, please refer to{' '}
                <a target="_blank" href={ACTIVATE_PAYMENT_METHODS_LINK}>
                  this
                </a>{' '}
                for detailed steps. For enabling other payment modes, please
                write to{' '}
                <a target="_blank" href="mailto:care@cashfree.com">
                  care@cashfree.com
                </a>{' '}
                or your account manager.
              </Text>
            }
            trigger={
              <span className="info pointer">
                <CustomIcons componentName="INFO" />
              </span>
            }
            position="right center"
          />
        </Space>
      );
    }
    default:
      return heading.displayText;
  }
};

export function PaymentMethods(props) {
  useInjectReducer({ key: 'paymentMethods', reducer });
  useInjectSaga({ key: 'paymentMethods', saga });

  const {
    enableLoader,
    getPaymentMethods,
    getPaypalConfig,
    resetPaymentMethodCardLoader,
    initiatePaypal,
    activatePaypalCurrency,
    deactivatePaypalCurrency,
    updateDefaultPaypalCurrency,
    savePaypalData,
    twoFactorAuthentication,
    resetTwoFactorAuthentication,
    resetPaypalConfig,
    resetData,
    enablePaymentMethod,
    getPaytmData,
    getPaytmTestConfig,
    getPaytmCheckSumHash,
    addPaytmProdConfig,
    addPaytmTestConfig,
    resetPaytmWallet,
    showToast,
    getDocsPerPaymentMethod,
    saveUploadDocumentsForm,
    downloadDocument,
    paymentMethods: {
      isLoading,
      allModesData,
      paypalAccount,
      defaultPaypalCurrency,
      activePaypalCurrencies,
      paypalCurrencies,
      paymentMethodCardLoader,
      paytmProdConfig,
      paytmTestConfig,
      enablePaymentMethodsData,
      paymentMethodsByStatus,
    },
  } = props;

  const queryParams = useQuery();

  const queryString = {};

  queryParams.forEach((value, key) => {
    queryString[key] = value;
  });

  const { env, ...paypalCallbackData } = queryString;
  // MODAL STATE
  const [modalName, resetModalName] = useState('');
  const [modalData, resetModalData] = useState([]);

  const savePaypalDataFn = paypalData => {
    // show loader on the connect paypal account button
    resetPaymentMethodCardLoader(PAYPAL);
    // call the api for saving the paypal data
    savePaypalData({
      data: paypalData,
      successCb: () => getPaypalConfig({}),
      errorCb: () => resetPaymentMethodCardLoader(PAYPAL),
    });
  };

  const getPageData = () => {
    enableLoader();
    getPaymentMethods();
  };

  useEffect(() => {
    getPageData();
    // if paypal callback data exists
    if (paypalCallbackData && Object.keys(paypalCallbackData).length > 0) {
      savePaypalDataFn(paypalCallbackData);
    } else {
      resetPaymentMethodCardLoader(PAYPAL);
      getPaypalConfig({});
    }
    resetPaymentMethodCardLoader(PAYTM);
    getPaytmData();
  }, [env]);

  useEffect(() => {
    if (twoFactorAuthentication) {
      getPageData();
      resetTwoFactorAuthentication(false);
    }
    return () => resetData();
  }, [twoFactorAuthentication]);

  const openConfirmModal = type => {
    resetModalName(type);
  };

  const connectPaypalAccount = url => {
    window.open(url, '_blank');
    resetPaymentMethodCardLoader(PAYPAL);
  };

  const connectPaypal = () => {
    resetPaymentMethodCardLoader(PAYPAL);
    initiatePaypal({
      cb: connectPaypalAccount,
      errorCb: () => resetPaymentMethodCardLoader(PAYPAL),
    });
    Analytics.track(EVENTS.PAYMENT_METHODS.CONNECT_PAYPAL);
  };

  const resetPaymentMethod = type => {
    if (type === RESET_PAYPAL_MODAL) {
      resetPaymentMethodCardLoader(PAYPAL);
      resetPaypalConfig({
        successCb: () => getPaypalConfig({}),
        errorCb: () => resetPaymentMethodCardLoader(PAYPAL),
      });
      Analytics.track(EVENTS.PAYMENT_METHODS.RESET_PAYPAL);
    } else {
      resetPaymentMethodCardLoader(PAYTM);
      resetPaytmWallet({
        successCb: () => getPaytmData(),
        errorCb: () => resetPaymentMethodCardLoader(PAYTM),
      });
    }
    resetModalName('');
  };

  // MODAL FUNCTION
  const displayModal = data => {
    resetModalName('BANK_MODAL');
    resetModalData(data);
  };

  const closeModal = () => {
    resetModalName('');
    resetModalData([]);
  };

  // RENDER ICONS

  const renderPaymentMethodIcon = activeModes => (
    <Space
      className="payment-method-icon-wrapper"
      alignItems="center"
      gap={2}
      direction="row"
      wrap="wrap"
    >
      {activeModes.map(obj => {
        const IconComponent = () => (
          <CustomIcons
            componentName={PAYMENT_METHODS_ICON_MAPPER[obj.nick]}
            key={obj.nick}
          />
        );

        return PAYMENT_METHODS_ICON_MAPPER[obj.nick] ? (
          <Popup
            key={obj.code}
            content={obj.display}
            trigger={
              <div className="icon" key={obj.nick}>
                <IconComponent />
              </div>
            }
          />
        ) : null;
      })}
    </Space>
  );

  // GET MODES ACCORDING TO COLUMN
  // eslint-disable-next-line consistent-return
  const getModes = (key, subKey, filter) => {
    const activeModes = allModesData[key][subKey].filter(obj => obj[filter]);
    const activeModesNick = activeModes.reduce((a, c) => [...a, c.nick], []);
    const internationalCards = activeModes.filter(obj =>
      obj.display.includes('International'),
    );
    const nonInternationalCards = activeModes.filter(
      obj => !obj.display.includes('International'),
    );
    switch (key) {
      case 'DEBIT_CARD': {
        return (
          <Space alignItems="center" gap={0} direction="row" wrap="wrap">
            {renderPaymentMethodIcon(nonInternationalCards)}
            {internationalCards && internationalCards.length > 0 && (
              <Button
                link
                className="p-0"
                id="mh-auto"
                onClick={() => displayModal(internationalCards)}
              >
                <CustomIcons
                  componentName="INTERNATIONAL_CARDS"
                  key="internationalcards"
                />{' '}
                International Cards
              </Button>
            )}
          </Space>
        );
      }
      case 'CREDIT_CARD': {
        return (
          <Space alignItems="center" gap={0} direction="row" wrap="wrap">
            {renderPaymentMethodIcon(nonInternationalCards)}
            {internationalCards && internationalCards.length > 0 && (
              <Button
                link
                className="p-0"
                id="mh-auto"
                onClick={() => displayModal(internationalCards)}
              >
                <CustomIcons
                  componentName="INTERNATIONAL_CARDS"
                  key="internationalcards"
                />{' '}
                International Cards
              </Button>
            )}
          </Space>
        );
      }
      case 'CORPORATE_CREDIT_CARD': {
        const CorporateModalData = activeModes.filter(
          obj => !CORPORATE_CREDIT_CARD_PAYMENT_METHODS.includes(obj.nick),
        );
        const corporateIconsData = activeModes.filter(obj =>
          CORPORATE_CREDIT_CARD_PAYMENT_METHODS.includes(obj.nick),
        );
        return (
          <Space alignItems="center" gap={0} direction="row" wrap="wrap">
            {renderPaymentMethodIcon(corporateIconsData)}
            {activeModesNick && activeModesNick.length > 5 && (
              <Conditional
                if={activeModesNick.length - corporateIconsData.length > 0}
              >
                <Button
                  link
                  className="p-0"
                  onClick={() => displayModal(CorporateModalData)}
                >
                  + {activeModesNick.length - corporateIconsData.length} other
                  banks
                </Button>
              </Conditional>
            )}
          </Space>
        );
      }

      case 'NET_BANKING': {
        const netBankingModalData = activeModes.filter(
          obj => !NET_BANKING_PAYMENT_METHODS.includes(obj.nick),
        );

        const netBankingPaymentMethods = activeModes.filter(obj =>
          NET_BANKING_PAYMENT_METHODS.includes(obj.nick),
        );
        return (
          <Space alignItems="center" gap={0} direction="row" wrap="wrap">
            {renderPaymentMethodIcon(netBankingPaymentMethods)}
            <Conditional
              if={activeModes.length - netBankingPaymentMethods.length > 0}
            >
              <Button
                link
                className="p-0"
                onClick={() => displayModal(netBankingModalData)}
              >
                + {activeModes.length - netBankingPaymentMethods.length} other
                banks
              </Button>
            </Conditional>
          </Space>
        );
      }

      case 'CREDIT_CARD_EMI': {
        const creditCardEMIModalData = activeModes.filter(
          obj => !EMI_ON_CARD_PAYMENT_METHODS.includes(obj.nick),
        );

        const creditCardEMIMethods = activeModes.filter(obj =>
          EMI_ON_CARD_PAYMENT_METHODS.includes(obj.nick),
        );

        return (
          <Space alignItems="center" gap={0} direction="row" wrap="wrap">
            {renderPaymentMethodIcon(creditCardEMIMethods)}
            <Conditional
              if={activeModes.length - creditCardEMIMethods.length > 0}
            >
              <Button
                link
                className="p-0"
                onClick={() => displayModal(creditCardEMIModalData)}
              >
                + {activeModes.length - creditCardEMIMethods.length} other banks
              </Button>
            </Conditional>
          </Space>
        );
      }

      case 'UPI': {
        if (activeModes && activeModes.length > 0) {
          const upiPaymentModes = UPI_PAYMENT_METHODS.map(
            ({ code, displayText }) => ({
              code,
              nick: code,
              display: displayText,
            }),
          );
          return (
            // UPI_PAYMENT_METHODS
            <Space gap={0} direction="column">
              {renderPaymentMethodIcon(upiPaymentModes)}
              <Text variant="b14" color="bodyLight" className="mt-1">
                Payments via all valid UPI apps are accepted.
              </Text>
            </Space>
          );
        }
        break;
      }

      default: {
        return renderPaymentMethodIcon(activeModes);
      }
    }
  };

  const handleEnablePaymentMethodClick = () => {
    resetModalName('ENABLE_PAYMENT_METHOD');
    Analytics.track(EVENTS.PAYMENT_METHODS.ENABLE);
  };

  const enablePaymentMethodsSubmit = (mode, modeDetails = {}) => {
    const { isInstantActive = false } = modeDetails;
    closeModal();
    enableLoader();
    enablePaymentMethod({
      paymentModeCode: mode,
      isInstantActive,
      successCb: () => {
        Analytics.track(EVENTS.PAYMENT_METHODS.MODE_ENABLE, {
          paymentModeCode: mode,
          status: 'SUCCESS',
        });
        if (isInstantActive) {
          getPaymentMethods();
        } else {
          resetModalName('ENABLE_PAYMENT_METHOD_SUCCESS');
          resetModalData(modeDetails);
        }
      },
      errorCb: err =>
        Analytics.track(EVENTS.PAYMENT_METHODS.MODE_ENABLE, {
          paymentModeCode: mode,
          status: 'ERROR',
          message: err.message,
        }),
    });
  };

  const handleDocumentUpload = formData => {
    const { modeDetails, fieldsToBeUploaded, ...data } = formData;
    saveUploadDocumentsForm({
      data,
      successCb: () => {
        // show the request submitted successfully modal
        resetModalName('ENABLE_PAYMENT_METHOD_SUCCESS');
        resetModalData(modeDetails);
        Analytics.track(EVENTS.PAYMENT_METHODS.STEP_2_SUBMIT, {
          ...fieldsToBeUploaded,
          paymentMode: data.paymentModeCode,
          status: 'SUCCESS',
        });
      },
      errorCb: err => {
        Analytics.track(EVENTS.PAYMENT_METHODS.STEP_2_SUBMIT, {
          ...fieldsToBeUploaded,
          paymentMode: data.paymentModeCode,
          status: 'ERROR',
          message: err.message,
        });
      },
    });
  };

  const getEnablePaymentMethodSuccessContent = () =>
    modalData && modalData.display ? (
      <Space direction="column" gap={2}>
        <Text variant="b14" color="bodyLight" className="mt-1">
          Your request to enable{' '}
          <span className="text-dark w-600">
            {titleCase(modalData.display)}
          </span>{' '}
          payment mode has been registered successfully. It usually takes{' '}
          <span className="text-dark w-600">
            {modalData.activationTime.toLowerCase()}
          </span>{' '}
          for the payment provider to approve and activate it. A standard
          pricing of{' '}
          <span className="text-dark w-600">{modalData.standardPricing}</span>{' '}
          per transaction will be applicable.
        </Text>
        <Text variant="b14" color="bodyLight">
          Contact your account manager or write to{' '}
          <a href="mailto:care@cashfree.com">care@cashfree.com</a> for any
          further queries.
        </Text>
      </Space>
    ) : null;

  const onAlertModalClose = () => {
    closeModal();
    getPageData();
  };

  const checkPaymentMethodEnabled = code =>
    findIndex(get(allModesData, `${WALLET}.enabled`, []), ['code', code]) >= 0;

  const openHistory = () => {
    resetModalName('PAYMENT_METHOD_HISTORY');
    Analytics.track(EVENTS.PAYMENT_METHODS.VIEW_HISTORY);
  };

  const showRequestedModes =
    paymentMethodsByStatus[IN_REVIEW] &&
    Object.keys(paymentMethodsByStatus[IN_REVIEW]).length > 0;

  const showHistory =
    paymentMethodsByStatus[REJECTED] &&
    Object.keys(paymentMethodsByStatus[REJECTED]).length > 0;

  const headingData = showRequestedModes
    ? PAYMENT_METHODS_HEADING
    : PAYMENT_METHODS_HEADING.filter(obj => obj.key !== 'requestedModes');

  const onStandardPricingClick = () => {
    window.open(STANDARD_PRICING_LINK, '_blank');
    Analytics.track(EVENTS.PAYMENT_METHODS.STANDARD_PRICING);
  };

  return (
    <div className="payment-methods-container">
      <Loader active={isLoading} />
      <div className="description-container">
        <Text variant="b14" color="bodyLight">
          Payment methods that are applicable to your business are shown below.
          You can contact your account manager or check our{' '}
          <a onClick={onStandardPricingClick}>payments page</a> to know more
          about all the payment methods we support and their standard pricing.
        </Text>
        <Space alignItems="center" gap={2}>
          <Conditional if={showHistory}>
            <Button secondary onClick={openHistory}>
              History
            </Button>
          </Conditional>
          <Conditional if={Object.keys(enablePaymentMethodsData).length > 0}>
            <Button primary onClick={handleEnablePaymentMethodClick}>
              Enable Payment Mode
            </Button>
          </Conditional>
        </Space>
      </div>
      <div className="payment-methods-list-container">
        {/* <div className="payment-methods-list-header">
          {PAYMENT_METHODS_HEADING.map(({ key, displayText }) => (
            <div key={key} className="cell header">
              {displayText}
            </div>
          ))}
        </div> */}
        <Table fixed singleLine>
          <TableHeader>
            <TableRow>
              {headingData.map(heading => (
                <TableHeaderCell
                  key={heading.key}
                  $colWidth={heading.width}
                  textAlign={heading.align}
                >
                  {getHeadingText(heading)}
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.keys(allModesData).map(key => (
              <TableRow key={key}>
                <TableCell className="payment-method-table-cell">
                  {PAYMENT_METHODS[key]}
                </TableCell>
                <TableCell className="payment-method-table-cell">
                  {getModes(key, 'enabled', 'isActive')}
                </TableCell>
                <TableCell className="payment-method-table-cell">
                  {getModes(key, 'otherAvailable', 'enabled')}
                </TableCell>
                <Conditional if={showRequestedModes}>
                  <TableCell className="payment-method-table-cell">
                    {getModes(key, 'inReview', 'nick')}
                  </TableCell>
                </Conditional>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div>
        <div className="text-light">
          To use the below payment methods, connect the respective accounts with
          Cashfree and accept payments through these methods.
        </div>
        <div className="third-party-payment-methods-container">
          <Paypal
            name={PAYPAL}
            connectAccount={connectPaypal}
            data={{
              paypalAccount,
              defaultPaypalCurrency,
              activePaypalCurrencies,
              paypalCurrencies,
            }}
            loading={paymentMethodCardLoader[PAYPAL]}
            activatePaypalCurrency={activatePaypalCurrency}
            deactivatePaypalCurrency={deactivatePaypalCurrency}
            updateDefaultPaypalCurrency={updateDefaultPaypalCurrency}
            enableLoader={enableLoader}
            openConfirmModal={openConfirmModal}
          />
          <Paytm
            data={{
              paytmProdConfig,
              paytmTestConfig,
            }}
            loading={paymentMethodCardLoader[PAYTM]}
            getPaytmTestConfig={getPaytmTestConfig}
            getPaymentMethods={getPaymentMethods}
            getPaytmCheckSumHash={getPaytmCheckSumHash}
            addPaytmProdConfig={addPaytmProdConfig}
            addPaytmTestConfig={addPaytmTestConfig}
            // openConfirmModal={openConfirmModal}
            showToast={showToast}
            checkPaymentMethodEnabled={checkPaymentMethodEnabled}
          />
        </div>
      </div>
      <Conditional if={modalName === 'BANK_MODAL'}>
        <BankListModal open closeModal={closeModal} list={modalData} />
      </Conditional>
      <Conditional
        if={modalName === RESET_PAYPAL_MODAL || modalName === RESET_PAYTM_MODAL}
      >
        <ConfirmModal
          title={
            modalName === RESET_PAYPAL_MODAL
              ? 'Deactivate Paypal'
              : 'Deactivate Paytm Wallet'
          }
          danger
          confirmText="Deactivate"
          onClose={() => resetModalName('')}
          onConfirm={() => resetPaymentMethod(modalName)}
          confirmBtnEvent={`Danger_Button_${
            modalName === RESET_PAYPAL_MODAL
              ? 'Deactivate_Paypal'
              : 'Deactivate_Paytm_Wallet'
          }_Confirm`}
          closeBtnEvent={`Link_Button_${
            modalName === RESET_PAYPAL_MODAL
              ? 'Deactivate_Paypal'
              : 'Deactivate_Paytm_Wallet'
          }_Cancel`}
          closeCrossEvent={`${
            modalName === RESET_PAYPAL_MODAL
              ? 'Deactivate_Paypal'
              : 'Deactivate_Paytm_Wallet'
          }_Icon_Close`}
        >
          <Text variant="b14" color="bodyLight">
            Are you sure you want to deactivate{' '}
            {modalName === RESET_PAYPAL_MODAL ? 'Paypal' : 'Paytm Wallet'}?
          </Text>
          <Text className="mr-top-16" variant="b14" color="warning">
            You will have to redo the entire setup process again if you wish to
            activate this again in future.
          </Text>
        </ConfirmModal>
      </Conditional>
      <Conditional if={modalName === 'ENABLE_PAYMENT_METHOD'}>
        <EnablePaymentMethods
          closeModal={closeModal}
          paymentMethods={enablePaymentMethodsData}
          enablePaymentMethods={enablePaymentMethodsSubmit}
          onUpload={handleDocumentUpload}
          downloadDocument={downloadDocument}
          getDocsPerPaymentMethod={getDocsPerPaymentMethod}
        />
      </Conditional>
      <Conditional if={modalName === 'ENABLE_PAYMENT_METHOD_SUCCESS'}>
        <AlertModal
          type="success"
          title="Request Submitted Successfully"
          onClose={onAlertModalClose}
          eventNameOnClose="Request_Submitted_Successfully_Close"
        >
          {getEnablePaymentMethodSuccessContent()}
        </AlertModal>
      </Conditional>
      <Conditional if={modalName === 'PAYMENT_METHOD_HISTORY'}>
        <PaymentMethodHistory
          closeModal={closeModal}
          rejectedModes={paymentMethodsByStatus[REJECTED]}
        />
      </Conditional>
    </div>
  );
}

PaymentMethods.propTypes = {
  enableLoader: PropTypes.func.isRequired,
  getPaymentMethods: PropTypes.func.isRequired,
  resetPaypalConfig: PropTypes.func.isRequired,
  paymentMethods: PropTypes.object,
  getPaypalConfig: PropTypes.func,
  resetPaymentMethodCardLoader: PropTypes.func,
  initiatePaypal: PropTypes.func,
  activatePaypalCurrency: PropTypes.func,
  deactivatePaypalCurrency: PropTypes.func,
  updateDefaultPaypalCurrency: PropTypes.func,
  savePaypalData: PropTypes.func,
  twoFactorAuthentication: PropTypes.any,
  resetTwoFactorAuthentication: PropTypes.func.isRequired,
  resetData: PropTypes.func.isRequired,
  enablePaymentMethod: PropTypes.func.isRequired,
  getPaytmData: PropTypes.func.isRequired,
  getPaytmTestConfig: PropTypes.func.isRequired,
  getPaytmCheckSumHash: PropTypes.func.isRequired,
  addPaytmProdConfig: PropTypes.func.isRequired,
  addPaytmTestConfig: PropTypes.func.isRequired,
  resetPaytmWallet: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  getDocsPerPaymentMethod: PropTypes.func.isRequired,
  saveUploadDocumentsForm: PropTypes.func.isRequired,
  downloadDocument: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  paymentMethods: makeSelectPaymentMethods(),
  twoFactorAuthentication: twoFactorAuthenticationSelector(),
});

function mapDispatchToProps(dispatch) {
  return {
    enableLoader: () => dispatch(enableLoaderAction()),
    getPaymentMethods: data => dispatch(getPaymentMethodsAction(data)),
    getPaypalConfig: data => dispatch(getPaypalConfigAction(data)),
    resetData: () => dispatch(resetDataAction()),
    resetPaymentMethodCardLoader: data =>
      dispatch(resetPaymentMethodCardLoaderAction(data)),
    initiatePaypal: data => dispatch(initiatePaypalAction(data)),
    activatePaypalCurrency: data =>
      dispatch(activatePaypalCurrencyAction(data)),
    deactivatePaypalCurrency: data =>
      dispatch(deactivatePaypalCurrencyAction(data)),
    updateDefaultPaypalCurrency: data =>
      dispatch(updateDefaultPaypalCurrencyAction(data)),
    savePaypalData: data => dispatch(savePaypalDataAction(data)),
    resetTwoFactorAuthentication: data =>
      dispatch(resetTwoFactorAuthenticationValue(data)),
    resetPaypalConfig: data => dispatch(resetPaypalConfigAction(data)),
    enablePaymentMethod: data => dispatch(enablePaymentMethodAction(data)),
    getPaytmData: () => dispatch(getPaytmDataAction()),
    getPaytmTestConfig: () => dispatch(getPaytmTestConfigAction()),
    getPaytmCheckSumHash: data => dispatch(getPaytmCheckSumHashAction(data)),
    addPaytmProdConfig: data => dispatch(addPaytmProdConfigAction(data)),
    addPaytmTestConfig: data => dispatch(addPaytmTestConfigAction(data)),
    resetPaytmWallet: data => dispatch(resetPaytmWalletAction(data)),
    showToast: data => dispatch(showToastAction(data)),
    getDocsPerPaymentMethod: data =>
      dispatch(getDocsPerPaymentMethodAction(data)),
    saveUploadDocumentsForm: data =>
      dispatch(saveUploadDocumentsFormAction(data)),
    downloadDocument: data => dispatch(downloadDocumentAction(data)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(PaymentMethods);
